import { NgTemplateOutlet } from '@angular/common';
import { Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { SafeUrl } from "@angular/platform-browser";
import { LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { DialogRef, ForgeButtonModule, ForgeCircularProgressModule, ForgeToolbarModule } from "@tylertech/forge-angular";
import { LgdFormControlFactory, LgdPasswordComponent, LgdTextComponent } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { StringHelper } from "@tylertech/shared-module";
import { ExecutionResult } from "@tylertech/tyler-api-types";
import { catchError, EMPTY, lastValueFrom, map, of } from "rxjs";
import { Auth0ErrorResponse, LoginView } from "../../interfaces";
import { isErroredExecutionResult, LoginError, OpenIdService, RouteInfoService, SecurityService, SystemInfoService } from "../../services";

@Component({
    selector: 'login-dialog-component',
    templateUrl: './login-dialog.component.html',
    styleUrl: './login-dialog.component.scss',
    imports: [
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeCircularProgressModule,
        ForgeToolbarModule,
        LgdPasswordComponent,
        LgdTextComponent,
        NgTemplateOutlet,
        ReactiveFormsModule
    ]
})
export class LoginDialogComponent
    implements OnInit
{
    private readonly dialogRef = inject<DialogRef<LoginDialogComponent, boolean>>(DialogRef);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $securityService = inject(SecurityService);
    private readonly $openIdService = inject(OpenIdService);
    private readonly $routeInfoService = inject(RouteInfoService);

    private readonly log = getLoggerFor("LoginDialogComponent");
    readonly loginForm: FormGroup;

    protected usernameControl: AbstractControl<string>;
    protected passwordControl: AbstractControl<string>;
    protected loginPromise: Promise<void>;

    readonly loading = signal(false);
    readonly useTylerId = signal(false);
    readonly error = signal<string | undefined>(undefined);
    readonly tylerIdRoute = signal<SafeUrl | undefined>(undefined);

    constructor()
    {
        // TODO - why do we ever have quotes in the username value?
        const username = this.$securityService.Username?.replace(/"/g, "");

        this.usernameControl = LgdFormControlFactory.text(username, true, {});
        this.passwordControl = LgdFormControlFactory.text(undefined, true, {});

        this.loginForm = new FormGroup({
            username: this.usernameControl,
            password: this.passwordControl,
        });
    }

    private get tenant(): string
    {
        return this.$routeInfoService.tenant?.replace(/"/g, "");
    }

    @HostListener("keydown", ["$event"])
    onKeydown($event: KeyboardEvent): void
    {
        if ($event.key === "Enter")
        {
            this.login();
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    ngOnInit(): void
    {
        const systemInfo = this.$systemInfoService.systemInfo;
        if (systemInfo.useTylerId)
        {
            this.log.debug(`TylerId Authentication! starting popup signin process`);
            this.useTylerId.set(true);

            this.loading.set(true);
            this.loginPromise = this.$openIdService.loginPopup()
                .then(
                    () =>
                    {
                        this.log.debug(`TylerId Authentication! popup signin process resolved`);

                        const authenticate$ = this.$securityService.getTokenAfterTylerIdAuth()
                            .pipe(
                                map(success =>
                                {
                                    this.log.debug(`$securityService.getTokenAfterTylerIdAuth`, { success });
                                }),
                                catchError(error =>
                                {
                                    this.log.error(`$securityService.getTokenAfterTylerIdAuth => ${JSON.stringify(error)}`);
                                    // TODO(DO-7967) - should probably display error or message in template and provide a reload button......

                                    this.error.set(this.parseError(error));

                                    return EMPTY;
                                }));

                        return lastValueFrom(authenticate$)
                            .then(() =>
                            {
                                this.dialogRef.close(true);
                            });
                    },
                    error =>
                    {
                        this.log.error(`TylerId Authentication! popup signin rejected`, { error });
                        // TODO(DO-7967) - should probably display error or message in template and provide a reload button......

                        this.error.set(this.parseError(error));
                    })
                .finally(() =>
                {
                    this.log.debug(`TylerId Authentication! popup signin process completed`);
                    this.loading.set(false);

                    this.loginPromise = undefined;
                });
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    login(): void
    {
        if (this.loginForm.invalid)
        {
            return;
        }

        const credentials: LoginView = {
            Tenant: this.tenant,
            Username: this.usernameControl.value,
            Password: this.passwordControl.value,
        };

        this.loading.set(true);
        this.loginPromise = lastValueFrom(
            this.$securityService.login(credentials)
                .pipe(
                    map((_: LoginView) =>
                    {
                        return true;
                    }),
                    catchError((result: unknown) =>
                    {
                        if (result instanceof LoginError)
                        {
                            const rejectedResponse = result.response;
                            const executionResult = rejectedResponse.body;
                            this.error.set(this.parseError(executionResult));
                        }
                        else if (isErroredExecutionResult(result))
                        {
                            this.error.set(this.parseError(result));
                        }

                        this.log.warning("error logging in", result);
                        return of(false);
                    })))
            .then((_success: boolean) =>
            {
                if (_success)
                {
                    this.log.debug("successful login");
                    this.dialogRef.close(true);
                }
                else
                {
                    this.log.warning("login failed");
                    // TODO(DO-7967) - should we stay open?
                    // this.dialogRef.close(false);
                }
            })
            .finally(() =>
            {
                this.loading.set(false);
                this.loginPromise = undefined;
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    logout(): void
    {
        this.dialogRef.close(false);
    }

    private parseError<T = unknown>(result: ExecutionResult<T>): string
    {
        const message = result.ErrorMessage
            ?? result.ValidationMessage;
        const errorObj = StringHelper.parse<Auth0ErrorResponse>(message);
        return errorObj?.error_description
            ?? message;
    }
}
